// The overview table is capped at whatever height the viewport leaves below the chrome above it: a full
// one scrolls internally with its horizontal scrollbar on screen instead of below the fold, and one with
// few records keeps its natural height. CSS cannot express that, and the fixed heights in the stylesheet
// assume an amount of chrome that does not hold on every page.
//
// The height is a pure function of the layout (viewport, the table's top, the fixed bottom bar), which is
// what everything below the table amounts to on an overview page. Include the mixin in a datatable and
// bind `overviewTableStyle()` to the table element.
export default {
    data() {
        return {
            overviewTableHeight: null,
        };
    },

    mounted() {
        this.changeTableHeight();

        // only an overview table is fitted, so nothing else pays for a listener or an observer
        if (!this.isOverview) {
            return;
        }

        window.addEventListener("resize", this.changeTableHeight);

        // late layout keeps arriving after mount; a resize observer re-fits as it settles
        if (typeof ResizeObserver !== "undefined") {
            this._overviewTableObserver = new ResizeObserver(() => this.scheduleChangeTableHeight());
            this._overviewTableObserver.observe(document.body);
        }
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.changeTableHeight);

        if (this._overviewTableObserver) {
            this._overviewTableObserver.disconnect();
            this._overviewTableObserver = null;
        }

        if (this._overviewTableFrame) {
            window.cancelAnimationFrame(this._overviewTableFrame);
            this._overviewTableFrame = null;
        }
    },

    methods: {
        scheduleChangeTableHeight() {
            if (this._overviewTableFrame) {
                return;
            }

            this._overviewTableFrame = window.requestAnimationFrame(() => {
                this._overviewTableFrame = null;
                this.changeTableHeight();
            });
        },

        changeTableHeight() {
            if (!this.isOverview || !this.$el) {
                return;
            }

            const table = this.$el.querySelector(".bo-table");

            if (!table) {
                return;
            }

            // Pure geometry, no measuring of the document: the space the viewport leaves below the table's
            // top, less the fixed bottom bar the page reserves room for. Nothing else is under an overview
            // table - the pagination footer is fixed, and #app's 40px bottom margin (the bar's spacer) is
            // removed for these pages in the stylesheet. A pure function also means the observer and resize
            // triggers are no-ops when nothing moved, so the height cannot drift or oscillate.
            // document-relative, so the height does not change as the page is scrolled
            const tableTop = table.getBoundingClientRect().top + window.scrollY;
            const bottomBar = document.querySelector("#app-version");
            const bottomBarHeight = bottomBar ? Math.round(bottomBar.getBoundingClientRect().height) : 0;
            const height = Math.max(window.innerHeight - tableTop - bottomBarHeight, 200);

            if (height !== this.overviewTableHeight) {
                this.overviewTableHeight = height;
            }
        },

        overviewTableStyle() {
            if (!this.isOverview || !this.overviewTableHeight) {
                return {};
            }

            // a ceiling, not a height: a table with few records keeps its natural height (and the stylesheet's
            // own height, which `auto` overrides) instead of being stretched to fill the screen
            return {
                height: "auto",
                maxHeight: `${this.overviewTableHeight}px`,
            };
        },
    },
};
